import { Dialog } from "@headlessui/react";
import {
  IconMail,
  IconX,
  IconFileTypePdf,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import ContractInfoRow from "../../../../components/ContractInfoRow";
import Button from "../../../../components/Button";
import { connect, useDispatch } from "react-redux";
import React, { useEffect, useState } from "react";
import { getClaimRecord } from "../../../../store/claim/actions";
import { isScopeLoading } from "../../../../store/scope-loading/helpers";
import { RootState } from "../../../../store/root-reducer";
import { ClaimScope } from "../../../../store/claim/types";
import { ClaimStatus } from "../../../../store/user/types";
import { FaSpinner } from "react-icons/fa6";
import { convertToCRMDate } from "../../../../helper";
import ClaimSubmitTicketModal from "../../../../components/ClaimSubmitTicketModal";
import ClaimSendEmailModal from "../../../../components/ClaimSendEmailModel";

const statusbg: any = {
  [ClaimStatus.Draft]: "bg-gray-200 text-gray-600 border border-gray-600",
  [ClaimStatus.DraftReminder]:
    "bg-yellow-200 text-yellow-600 border border-yellow-600",
  [ClaimStatus.Adjudication]:
    "bg-blue-200 text-blue-600 border border-blue-600",
  [ClaimStatus.CustomerAction]:
    "bg-orange-200 text-orange-600 border border-orange-600",
  [ClaimStatus.CARResponse]:
    "bg-indigo-200 text-indigo-600 border border-indigo-600",
  [ClaimStatus.Approved]: "bg-green-200 text-green-600 border border-green-600",
  [ClaimStatus.Disposition]:
    "bg-purple-200 text-purple-600 border border-purple-600",
  [ClaimStatus.NonMonetary]: "bg-teal-200 text-teal-600 border border-teal-600",
  [ClaimStatus.StoreReferral]:
    "bg-amber-200 text-amber-600 border border-amber-600",
  [ClaimStatus.SoftClose]: "bg-cyan-200 text-cyan-600 border border-cyan-600",
  [ClaimStatus.Closed]:
    "bg-emerald-200 text-emerald-600 border border-emerald-600",
  [ClaimStatus.Denied]: "bg-red-200 text-red-600 border border-red-600",
  [ClaimStatus.ClaimedDeniedCustomerComm]:
    "bg-rose-200 text-rose-600 border border-rose-600",
  [ClaimStatus.Abandoned]:
    "bg-slate-200 text-slate-600 border border-slate-600",
};

function ClaimViewModal({
  isOpen,
  close,
  data,
  loading,
  claimId,
  status,
  claimNumber,
}) {
  const { REACT_APP_CLAIM_LANDING_URL } = process.env;
  const dispatch = useDispatch();

  const [openSubmitModal, setOpenSubmitModal] = useState(false);
  const [openSendEmailModal, setOpenSendEmailModal] = useState(false);
  const [expandedEmails, setExpandedEmails] = useState<number[]>([]);

  const toggleEmail = (index: number) => {
    setExpandedEmails((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  useEffect(() => {
    if (claimId && claimId !== data.id) {
      dispatch(getClaimRecord(claimId));
    }
  }, [claimId, data.id, dispatch]);

  const consumerDocs =
    data.documents?.filter((doc: any) =>
      doc.key?.includes("photos-consumer")
    ) || [];
  const techDocs =
    data.documents?.filter((doc: any) => doc.key?.includes("tech-doc")) || [];

  const renderDocument = (doc: any, i: number) => {
    const isPdf = doc.fileName?.toLowerCase().endsWith(".pdf");
    return isPdf ? (
      <a
        key={i}
        href={doc.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex flex-col items-center justify-center space-x-2 text-gray-600 size-28 cursor-pointer border border-gray-300 rounded-md"
      >
        <IconFileTypePdf stroke={2} className="text-center" size={80} />
        <span className="text-xs text-center truncate w-full px-1">
          {doc.fileName || "Document"}
        </span>
      </a>
    ) : (
      <img
        key={i}
        src={doc.url}
        alt="document"
        className="size-28 rounded-md object-cover cursor-pointer border border-gray-300"
        onClick={() => {
          window.open(doc.url, "_blank");
        }}
      />
    );
  };

  return (
    <>
      <Dialog
        open={isOpen}
        as="div"
        className="relative z-10 focus:outline-none"
        onClose={close}
      >
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto bg-black/50">
          <div className="flex h-screen overflow-hidden items-center justify-center p-4">
            <div className="w-full max-h-full overflow-hidden rounded-xl bg-white max-w-7xl flex flex-col">
              <div className="flex items-center justify-between border-b border-gray-200 gap-3 p-3 text-gray-800">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-medium">
                    {claimNumber || "N/A"}
                  </h2>
                  {status && (
                    <div
                      className={`inline-block items-center justify-center py-0.5 px-3 rounded-2xl text-[12px] ${statusbg[status]}`}
                      role="presentation"
                    >
                      <span>{status}</span>
                    </div>
                  )}
                </div>
                <button onClick={close}>
                  <IconX />
                </button>
              </div>
              {loading ? (
                <div className="flex items-center justify-center h-full flex-1 min-h-72">
                  <FaSpinner className="animate-spin text-primary size-8" />
                </div>
              ) : (
                <div className="space-y-4 py-4 overflow-y-auto h-full container">
                  <div className="space-y-1">
                    <div className="flex justify-between items-center">
                      <p className="font-medium text-black">
                        Contact Information
                      </p>
                      <div className="flex items-center gap-3">
                        <Button
                          variant="gray"
                          onClick={() =>
                            window.open(
                              `${REACT_APP_CLAIM_LANDING_URL}/${data.status_code}`,
                              "_blank",
                              "noreferrer"
                            )
                          }
                        >
                          Customer Landing Page
                        </Button>
                        <Button
                          variant="blue"
                          onClick={() => {
                            setOpenSubmitModal(true);
                          }}
                        >
                          Submit Ticket
                        </Button>
                        <Button
                          variant="gray"
                          onClick={() => {
                            setOpenSendEmailModal(true);
                          }}
                          disabled={!data.email}
                        >
                          Send Claim Landing Page To Customer
                        </Button>
                      </div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden px-3 py-4 gap-x-5 gap-y-3 grid min-[1040px]:grid-cols-3 sm:grid-cols-2 grid-cols-1">
                      <ContractInfoRow
                        label="Email :"
                        value={data.email || "N/A"}
                      />
                      <ContractInfoRow
                        label="Phone number :"
                        value={data.phoneNumber || "N/A"}
                        isPhone
                      />
                      <ContractInfoRow
                        label="Address 1 :"
                        value={data.address1 || "N/A"}
                      />
                      <ContractInfoRow
                        label="Address 2 :"
                        value={data.address2 || "N/A"}
                      />
                      <ContractInfoRow
                        label="City :"
                        value={data.city || "N/A"}
                      />
                      <ContractInfoRow
                        label="State :"
                        value={data.state || "N/A"}
                      />
                      <ContractInfoRow
                        label="Zip :"
                        value={data.zip || "N/A"}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <p className="font-medium text-black">
                      {data.WarrantyProgramName + " "}
                      <span className="text-gray-500">
                        (Claim ID:{data.claimNumber || ""})
                      </span>
                    </p>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden px-3 py-4 gap-x-5 gap-y-3 grid min-[1040px]:grid-cols-3 sm:grid-cols-2 grid-cols-1">
                      <ContractInfoRow
                        label="Claim Filed Date :"
                        value={data.claimFileDate || ""}
                      />
                      <ContractInfoRow
                        label="Plan Number :"
                        value={data.planNumber || ""}
                      />
                      <ContractInfoRow
                        label="Dealer :"
                        value={data.dealer || ""}
                      />
                      <ContractInfoRow
                        label="Purchase Date :"
                        value={convertToCRMDate(data.purchaseDate) || ""}
                      />
                      <ContractInfoRow
                        label="Delivery Date :"
                        value={convertToCRMDate(data.deliveryDate) || ""}
                      />
                      <ContractInfoRow
                        label="Plan Agreement (PDF) :"
                        value={data.planAgreement || ""}
                        isLink
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <p className="font-medium text-black">
                      Item in Claim:{" "}
                      <span className="text-gray-500">
                        {data.claimItems?.length || ""}
                      </span>
                    </p>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden px-3 py-4 space-y-5">
                      {data.claimItems && data.claimItems?.length > 0 ? (
                        data.claimItems?.map((item, index) => (
                          <div key={index}>
                            <p className="bg-gray-200 px-2 py-1.5 flex items-center gap-3">
                              {index + 1}. {item.claim_item_name}{" "}
                              {item.status && (
                                <span className="rounded-3xl py-0.5 px-3 text-[12px] inline-block bg-yellow-200 text-yellow-600 border border-yellow-600">
                                  {item.status}
                                </span>
                              )}
                            </p>
                            <div className="gap-x-5 gap-y-3 grid min-[1040px]:grid-cols-3 sm:grid-cols-2 grid-cols-1 mt-3">
                              <ContractInfoRow
                                label="Manufacturer :"
                                value={item.manufacturer || ""}
                              />
                              <ContractInfoRow
                                label="Color :"
                                value={item.color || ""}
                              />
                              <ContractInfoRow
                                label="Material :"
                                value={item.material || ""}
                              />
                              <ContractInfoRow
                                label="Vendor :"
                                value={item.vendor || "-"}
                              />
                              <ContractInfoRow
                                label="Description :"
                                value={item.description || "-"}
                              />
                            </div>

                            {item.damages?.map((damage, dIndex) => (
                              <div className="mt-5" key={dIndex}>
                                <div className="flex items-center gap-3 mb-2">
                                  <p className="font-medium text-sm text-indigo-500">
                                    Damage {dIndex + 1}
                                  </p>
                                  {damage.bp_status && (
                                    <span
                                      className={`rounded-3xl py-0.5 px-3 text-[12px] inline-block ${
                                        statusbg[damage.bp_status] ||
                                        "bg-gray-200 text-gray-600 border border-gray-600"
                                      }`}
                                    >
                                      {damage.bp_status}
                                    </span>
                                  )}
                                </div>
                                <div className="gap-x-5 gap-y-3 grid min-[1040px]:grid-cols-3 sm:grid-cols-2 grid-cols-1">
                                  <ContractInfoRow
                                    label="Damage Category :"
                                    value={damage.damage_category || ""}
                                  />
                                  <ContractInfoRow
                                    label="Specific Damage :"
                                    value={
                                      damage.specific_damage?.toString() || ""
                                    }
                                  />
                                  <ContractInfoRow
                                    label="Damage Area :"
                                    value={damage.damage_Area || ""}
                                  />
                                  <ContractInfoRow
                                    label="When did it happen :"
                                    value={damage.when_did_it_happen || ""}
                                  />
                                  <ContractInfoRow
                                    label="How did it happen :"
                                    value={damage.how_did_it_happen || ""}
                                  />
                                  <ContractInfoRow
                                    label="Steps Taken to Remedy :"
                                    value={
                                      damage.did_you_take_any_steps_to_remedy ||
                                      ""
                                    }
                                  />
                                  <ContractInfoRow
                                    label="Notes :"
                                    value={damage.notes || ""}
                                  />
                                  {/* {damage.bp_status === "Declined" && ( */}
                                  <ContractInfoRow
                                    label="Declined Reason :"
                                    value={damage.denial_short_summary || ""}
                                  />
                                  {/* )}
                                  {damage.bp_status === "Declined" && ( */}
                                  <ContractInfoRow
                                    label="Declined Code :"
                                    value={
                                      damage.denial_reason_code_lookup || ""
                                    }
                                  />
                                  {/* )} */}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4 w-full">
                          <p>No items in claim</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="font-medium text-black">Documents</p>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden px-4 py-4 space-y-6">
                      <div className="space-y-2">
                        <p className="font-medium text-sm text-gray-700">
                          Consumer Photos
                        </p>
                        <div className="flex flex-wrap gap-4">
                          {consumerDocs.length > 0 ? (
                            consumerDocs.map(renderDocument)
                          ) : (
                            <div className="text-center py-2 w-full text-gray-500 text-sm">
                              <p>No consumer photos found</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <p className="font-medium text-sm text-gray-700">
                          Tech Documents
                        </p>
                        <div className="flex flex-wrap gap-4">
                          {techDocs.length > 0 ? (
                            techDocs.map(renderDocument)
                          ) : (
                            <div className="text-center py-2 w-full text-gray-500 text-sm">
                              <p>No tech documents found</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <p className="font-medium text-black">
                      Customer Action Request
                    </p>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden px-3 py-4">
                      <ContractInfoRow
                        label="Customer Action Detail :"
                        value={data.customerActionDetail || "-"}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-black">Emails</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                          <thead className="bg-gray-50 text-[11px] text-gray-500 uppercase tracking-wider">
                            <tr>
                              <th className="px-4 py-3 font-semibold">
                                Subject
                              </th>
                              <th className="px-4 py-3 font-semibold text-center whitespace-nowrap">
                                Date
                              </th>
                              <th className="px-4 py-3 font-semibold text-center">
                                Source
                              </th>
                              <th className="px-4 py-3 font-semibold text-center">
                                Sent By
                              </th>
                              <th className="px-4 py-3 font-semibold text-center">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {data.emails && data.emails.length > 0 ? (
                              data.emails.map((email, i) => (
                                <React.Fragment key={i}>
                                  <tr
                                    className="hover:bg-gray-50 transition-colors group cursor-pointer"
                                    onClick={() => toggleEmail(i)}
                                  >
                                    <td className="px-4 py-3">
                                      <div className="flex items-start gap-3">
                                        <div className="mt-1">
                                          <IconMail
                                            size={18}
                                            className="text-gray-500"
                                          />
                                        </div>
                                        <div className="flex flex-col">
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-900 truncate max-w-xs">
                                              {email.subject || "(No Subject)"}
                                            </span>
                                            {(email.content ||
                                              email.summary) && (
                                              <span>
                                                {expandedEmails.includes(i) ? (
                                                  <IconChevronUp
                                                    size={14}
                                                    className="text-gray-400"
                                                  />
                                                ) : (
                                                  <IconChevronDown
                                                    size={14}
                                                    className="text-gray-400"
                                                  />
                                                )}
                                              </span>
                                            )}
                                          </div>
                                          <span className="text-[11px] text-gray-400 truncate max-w-xs">
                                            {email.to?.[0]?.email || "N/A"}
                                          </span>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap text-center">
                                      {email.time
                                        ? new Date(email.time).toLocaleString()
                                        : "N/A"}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-center text-gray-600 capitalize">
                                      {email.source || "N/A"}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-center text-gray-600">
                                      {email.from?.user_name || "N/A"}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <div className="flex flex-wrap justify-center gap-1">
                                        {email.status?.map(
                                          (st: any, idx: number) => (
                                            <span
                                              key={idx}
                                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium border ${
                                                email.sent
                                                  ? "bg-green-50 text-green-700 border-green-200"
                                                  : "bg-blue-50 text-blue-700 border-blue-200"
                                              }`}
                                            >
                                              {st.type}
                                            </span>
                                          )
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                  {expandedEmails.includes(i) && (
                                    <tr className="bg-gray-50/50">
                                      <td colSpan={5} className="px-4 py-4">
                                        <div className="bg-white border border-gray-200 rounded-lg p-4 ml-8">
                                          <div
                                            className="text-sm text-gray-700 break-words overflow-auto max-h-[400px] email-body-content"
                                            dangerouslySetInnerHTML={{
                                              __html:
                                                email.content ||
                                                email.summary ||
                                                "No content available",
                                            }}
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan={5}
                                  className="px-4 py-12 text-center text-gray-500 italic text-sm"
                                >
                                  No email communications found for this claim.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  {openSubmitModal && (
                    <ClaimSubmitTicketModal
                      isOpen={openSubmitModal}
                      onClose={() => setOpenSubmitModal(false)}
                    />
                  )}
                  {openSendEmailModal && (
                    <ClaimSendEmailModal
                      isOpen={openSendEmailModal}
                      onClose={() => setOpenSendEmailModal(false)}
                      customerEmail={data.email || ""}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}

const mapStateToProps = (state: RootState) => ({
  data: state.claim || {},
  loading: isScopeLoading(state.scopeLoading, ClaimScope),
});

export default connect(mapStateToProps)(ClaimViewModal);
